import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGlobalContext } from 'context/GlobalContext';
import environment from 'constants/apiConst';
import queryEnv from 'constants/queryConst';

const useServerItemSearch = (endpoint, resourceIdentifier, queryKey, orderBy = { value: "ASC", label: "Ascendente" }, enabled = true) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { requestData } = useGlobalContext();

  // Query para obtener todos los datos cuando no hay búsqueda
  // DESHABILITADA: No hacer petición automática para evitar duplicados
  // Los datos se obtienen de las queries principales (useQuery/useInfiniteQuery)
  const { data: allData, isLoading: isLoadingAll } = useQuery({
    queryKey: [queryKey, 'all', resourceIdentifier, orderBy],
    queryFn: () =>
      resourceIdentifier &&
      requestData({
        Endpoint: endpoint,
        resourceIdentifier: resourceIdentifier,
        pageParam: 1,
        data: {
          orderBy: orderBy.value,
          length: 1000, // Usar 'length' en lugar de 'per_page' para consistencia
        },
        method: "POST",
      }),
    enabled: false, // Deshabilitado para evitar peticiones duplicadas - solo se usa cuando hay búsqueda activa
  });

  // Función para buscar en todas las páginas del servidor
  const searchInAllPages = useCallback(async (itemCode) => {
    if (!itemCode || !resourceIdentifier) return [];

    // setIsSearching(true);
    const results = [];
    let currentPage = 1;
    let hasMorePages = true;

    try {
      // while (hasMorePages) {
        const response = await requestData({
          Endpoint: endpoint,
          resourceIdentifier: resourceIdentifier,
          pageParam: currentPage,
          data: {
            orderBy: orderBy.value,
            length: 50, // Usar 'length' en lugar de 'per_page' para consistencia
            item_code: itemCode,
          },
          method: "POST",
        });

        if (response?.data?.status?.code === 200) {
          const pageData = response.data.status.data.data || [];
          
          // Buscar el item_code en esta página
          const foundItems = pageData.filter(item => 
            item.item_code === parseInt(itemCode) || 
            item.item_code === itemCode ||
            String(item.item_code) === String(itemCode)
          );
          
          results.push(...pageData);

          // Verificar si hay más páginas
          const lastPage = response.data.status.data.last_page;
          hasMorePages = currentPage < lastPage;
          currentPage++;
        } else {
          hasMorePages = false;
        }
      // }
    } catch (error) {
      console.error('Error searching in server:', error);
    } finally {
      // setIsSearching(false);
    }

    return results;
  }, [endpoint, resourceIdentifier, orderBy, requestData]);

  // Memoizar los datos filtrados
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      // Si no hay búsqueda, retornar array vacío
      // Los datos se obtienen de las queries principales, no de aquí
      return [];
    }

    // Si hay búsqueda, usar los resultados de la búsqueda
    return searchResults;
  }, [searchTerm, searchResults]);

  // Función para manejar la búsqueda
  const handleSearch = useCallback(async (term) => {
    setSearchTerm(term);
    
    if (term.trim()) {
      const results = await searchInAllPages(term.trim());
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchInAllPages]);

  // Función para limpiar la búsqueda
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
  }, []);

  return {
    searchTerm,
    filteredData,
    handleSearch,
    clearSearch,
    hasSearch: searchTerm.trim().length > 0,
    isLoading: isLoadingAll || isSearching,
    isSearching,
  };
};

export default useServerItemSearch;
